/**
 * Toast Notification Manager
 * Provides a centralized way to show toast notifications throughout the app
 */

class ToastManager {
  constructor() {
    this.toasts = [];
    this.listeners = [];
    this.maxToasts = 5;
  }

  /**
   * Subscribe to toast updates
   * @param {Function} callback - Function to call when toasts change
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notify all listeners of toast changes
   */
  notify() {
    this.listeners.forEach(listener => listener([...this.toasts]));
  }

  /**
   * Show a toast notification
   * @param {string} message - Toast message
   * @param {string} type - Toast type: 'success', 'error', 'warning', 'info'
   * @param {number} duration - Duration in milliseconds (default: 3000)
   */
  show(message, type = 'info', duration = 3000) {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration };
    
    this.toasts.push(toast);
    
    // Limit the number of toasts
    if (this.toasts.length > this.maxToasts) {
      this.toasts.shift();
    }
    
    this.notify();
    
    // Auto-remove after duration
    setTimeout(() => {
      this.remove(id);
    }, duration);
    
    return id;
  }

  /**
   * Remove a toast by ID
   * @param {number} id - Toast ID
   */
  remove(id) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notify();
  }

  /**
   * Clear all toasts
   */
  clear() {
    this.toasts = [];
    this.notify();
  }

  // Convenience methods
  success(message, duration) {
    return this.show(message, 'success', duration);
  }

  error(message, duration) {
    return this.show(message, 'error', duration || 5000); // Errors stay longer
  }

  warning(message, duration) {
    return this.show(message, 'warning', duration);
  }

  info(message, duration) {
    return this.show(message, 'info', duration);
  }
}

// Export singleton instance
export const toastManager = new ToastManager();
export default toastManager;
